<template>
  <div class="eval-student">

    <!-- ============ 登录 ============ -->
    <div class="center-wrap" v-if="page === 'login'">
      <el-card class="login-card" shadow="always">
        <h1 class="title">学生评价老师</h1>
        <p class="sub">{{ batchName }}</p>

        <el-form @submit.native.prevent>
          <el-form-item label="账号（身份证号）">
            <el-input v-model.trim="loginForm.account" maxlength="18"
                      placeholder="请输入身份证号" @keyup.enter.native="doLogin"></el-input>
          </el-form-item>
          <el-form-item label="密码">
            <el-input v-model="loginForm.password" type="password" show-password
                      placeholder="初始密码为身份证后6位" @keyup.enter.native="doLogin"></el-input>
          </el-form-item>
          <el-button type="primary" class="block-btn" :loading="loading" @click="doLogin">登 录</el-button>
        </el-form>

        <p class="tip">账号是身份证号，初始密码为身份证后 6 位，登录后需修改密码。</p>
      </el-card>
    </div>

    <!-- ============ 首次登录改密 ============ -->
    <div class="center-wrap" v-else-if="page === 'pwd'">
      <el-card class="login-card" shadow="always">
        <h1 class="title">请修改初始密码</h1>
        <p class="sub">{{ student.name }} 同学，为了账号安全，请设置新密码</p>

        <el-form @submit.native.prevent>
          <el-form-item label="新密码（6-20位）">
            <el-input v-model="pwdForm.newPassword" type="password" show-password placeholder="请输入新密码"></el-input>
          </el-form-item>
          <el-form-item label="确认新密码">
            <el-input v-model="pwdForm.confirmPassword" type="password" show-password
                      placeholder="请再次输入" @keyup.enter.native="doChangePwd"></el-input>
          </el-form-item>
          <el-button type="primary" class="block-btn" :loading="loading" @click="doChangePwd">确认修改</el-button>
        </el-form>

        <p class="tip">新密码不能与身份证后 6 位相同，请牢记，忘记需找教务处重置。</p>
      </el-card>
    </div>

    <!-- ============ 评价 ============ -->
    <div v-else class="eval-main">

      <header class="topbar">
        <div class="topbar-inner">
          <div class="who">
            <strong>{{ student.name }}</strong>
            <el-tag size="small">{{ student.class_name }}</el-tag>
          </div>
          <div class="right">
            <span class="batch">{{ batch.name }}</span>
            <el-link :underline="false" @click="logout">退出</el-link>
          </div>
        </div>
      </header>

      <main class="body">
        <div class="notice">
          <div>
            <b>请同学们放心地，客观地，公正地评价老师，你的评价是保密的，我们不会透露给任何人。每周上报教师提问次数及导师谈话的次数，每月对老师的教学进行一次评价</b>
            <span class="muted" v-if="period.week_no">
              本学期第 {{ period.week_no }} 周（{{ shortDate(period.week_start) }} ~ {{ shortDate(period.week_end) }}）
              · 已累计上报 {{ period.weeks_reported }} 周 · 已评价 {{ period.months_evaluated }} 个月
            </span>
          </div>
          <span class="muted" v-if="!batch.running">当前不在评价时间内，仅可查看</span>
        </div>

        <el-tabs v-model="activeTab" class="eval-tabs">

          <!-- ============ 页签一：每周提问上报 ============ -->
          <el-tab-pane name="ask">
            <span slot="label">
              <i class="el-icon-chat-dot-square"></i> 教师提问
              <el-badge v-if="askFilledCount < teachers.length" is-dot class="tab-dot"></el-badge>
              <i v-else class="el-icon-circle-check tab-ok"></i>
            </span>

            <div class="notice slim">
              <div>
                <b>本周（{{ shortDate(period.week_start) }} ~ {{ shortDate(period.week_end) }}）各位老师提问了你几次？</b>
                <span class="muted">共 {{ teachers.length }} 位老师，已填 {{ askFilledCount }} 位，没有就被填 0</span>
                <span class="muted" v-if="askWindow.deadline_text">
                  上报时间：{{ askWindow.open_text }} ~ {{ askWindow.deadline_text }}
                </span>
              </div>
              <el-tag size="small" v-if="askWindow.status" :type="askWindow.status === 'open' ? 'success' : 'info'">
                {{ askWindow.status === 'open' ? '开放中' : askWindow.status === 'pending' ? '未开始' : '已截止' }}
              </el-tag>
            </div>

            <el-card class="teacher-card" shadow="hover" v-for="(t, ti) in teachers" :key="'a' + t.teacher_info_id">
              <div slot="header" class="t-head">
                <span class="t-index">{{ ti + 1 }}</span>
                <span class="t-name">{{ t.name }}</span>
                <el-tag size="small" type="info" v-if="t.course_names">{{ t.course_names }}</el-tag>
                <el-tag size="small" type="success" class="done-flag" v-if="t.ask_done">本周已上报</el-tag>
              </div>

              <div class="ask-row">
                <span class="ask">本周该老师提问我</span>
                <el-input-number v-model="t.ask_input" :min="0" :max="99" size="small"
                                 :disabled="!batch.running || askWindow.status !== 'open'" controls-position="right"></el-input-number>
                <span class="ask">次</span>
                <span class="muted total-tip" v-if="t.ask_total > 0">本学期累计 {{ t.ask_total }} 次</span>
              </div>
            </el-card>

            <p class="empty" v-if="teachers.length === 0">暂未查到你的任课教师，请联系教务处。</p>
          </el-tab-pane>

          <!-- ============ 页签二：每周导师谈话 ============ -->
          <el-tab-pane name="talk">
            <span slot="label">
              <i class="el-icon-user"></i> 导师谈话
              <el-badge v-if="!talk.done" is-dot class="tab-dot"></el-badge>
              <i v-else class="el-icon-circle-check tab-ok"></i>
            </span>

            <div class="notice slim">
              <div>
                <b>本周（{{ shortDate(period.week_start) }} ~ {{ shortDate(period.week_end) }}）与导师的谈话情况</b>
                <span class="muted">每位学生只有一位导师，选定后以后各周自动带出；每周填报一次</span>
                <span class="muted" v-if="askWindow.deadline_text">
                  上报时间：{{ askWindow.open_text }} ~ {{ askWindow.deadline_text }}
                </span>
              </div>
              <el-tag size="small" v-if="askWindow.status" :type="askWindow.status === 'open' ? 'success' : 'info'">
                {{ askWindow.status === 'open' ? '开放中' : askWindow.status === 'pending' ? '未开始' : '已截止' }}
              </el-tag>
            </div>

            <el-card class="teacher-card" shadow="hover">
              <div slot="header" class="t-head">
                <span class="t-name">导师谈话记录</span>
                <el-tag size="small" type="success" class="done-flag" v-if="talk.done">本周已填报</el-tag>
              </div>

              <div class="talk-row">
                <span class="talk-label">我的导师</span>
                <el-select v-model="talkForm.mentor_name" size="small" filterable clearable
                           placeholder="请从本班教师中选择" style="width:220px"
                           :disabled="!batch.running || askWindow.status !== 'open'"
                           no-data-text="未查到本班任课教师，请联系教务处">
                  <el-option v-for="m in mentorOptions" :key="m.name" :label="m.label" :value="m.name">
                  </el-option>
                </el-select>
                <span class="muted" v-if="talk.weeks_reported > 0">本学期已累计填报 {{ talk.weeks_reported }} 周</span>
              </div>

              <div class="talk-row">
                <span class="talk-label">本周谈话次数</span>
                <el-input-number v-model="talkForm.talk_count" :min="0" :max="99" size="small"
                                 controls-position="right"
                                 :disabled="!batch.running || askWindow.status !== 'open'"></el-input-number>
                <span class="ask">次</span>
              </div>

              <div class="talk-row talk-content">
                <span class="talk-label">谈话内容</span>
                <el-input v-model="talkForm.content" type="textarea" :rows="3" maxlength="200" show-word-limit
                          placeholder="简要写一下这周和导师谈了什么，比如学习状态、心理、生涯规划等"
                          :disabled="!batch.running || askWindow.status !== 'open'"></el-input>
              </div>
            </el-card>
          </el-tab-pane>

          <!-- ============ 页签三：每月教学评价 ============ -->
          <el-tab-pane name="eval">
            <span slot="label">
              <i class="el-icon-star-off"></i> 教学评价
              <el-badge v-if="evalFilledCount < teachers.length" is-dot class="tab-dot"></el-badge>
              <i v-else class="el-icon-circle-check tab-ok"></i>
            </span>

            <div class="notice slim">
              <div>
                <b>{{ period.month }} 月教学常规评价</b>
                <span class="muted">每月评价一次，共 {{ teachers.length }} 位老师，已评好 {{ evalFilledCount }} 位</span>
                <span class="muted" v-if="evalWindow.deadline_text">
                  评价时间：{{ evalWindow.open_text }} ~ {{ evalWindow.deadline_text }}
                </span>
              </div>
              <el-tag size="small" v-if="evalWindow.status" :type="evalWindow.status === 'open' ? 'success' : 'info'">
                {{ evalWindow.status === 'open' ? '开放中' : evalWindow.status === 'pending' ? '未开始' : '已截止' }}
              </el-tag>
            </div>

            <el-card class="teacher-card" shadow="hover" v-for="(t, ti) in teachers" :key="'e' + t.teacher_info_id">
              <div slot="header" class="t-head">
                <span class="t-index">{{ ti + 1 }}</span>
                <span class="t-name">{{ t.name }}</span>
                <el-tag size="small" type="info" v-if="t.course_names">{{ t.course_names }}</el-tag>
                <el-tag size="small" type="success" class="done-flag" v-if="t.submitted">本月已评</el-tag>
              </div>

              <div class="item-row" v-for="it in items" :key="it.id">
                <span class="item-name">{{ it.name }}</span>
                <el-rate v-model="t.scores[it.id]" :max="it.max_score"
                         :disabled="!batch.running || evalWindow.status !== 'open'"></el-rate>
              </div>

              <div class="extra">
                <el-input class="comment" size="small" maxlength="100" v-model="t.comment"
                          placeholder="对该老师的意见或建议（选填）"
                          :disabled="!batch.running || evalWindow.status !== 'open'"></el-input>
              </div>
            </el-card>
          </el-tab-pane>
        </el-tabs>

        <div style="height:80px"></div>
      </main>

      <div class="footer-bar">
        <div class="footer-inner" v-if="activeTab === 'ask'">
          <span class="muted">
            本周已填 {{ askFilledCount }} / {{ teachers.length }}
            <template v-if="askWindow.deadline_text"> · 上报 {{ askWindow.open_text }} ~ {{ askWindow.deadline_text }}</template>
          </span>
          <el-button type="primary" :loading="loading"
                     :disabled="!batch.running || askWindow.status !== 'open'" @click="doSubmitAsk">
            {{ askBtnText }}
          </el-button>
        </div>
        <div class="footer-inner" v-else-if="activeTab === 'talk'">
          <span class="muted">
            {{ talk.done ? '本周已填报，可修改后重新提交' : '本周尚未填报导师谈话' }}
            <template v-if="askWindow.deadline_text"> · 上报 {{ askWindow.open_text }} ~ {{ askWindow.deadline_text }}</template>
          </span>
          <el-button type="primary" :loading="loading"
                     :disabled="!batch.running || askWindow.status !== 'open'" @click="doSubmitTalk">
            {{ talkBtnText }}
          </el-button>
        </div>
        <div class="footer-inner" v-else>
          <span class="muted">
            本月已评好 {{ evalFilledCount }} / {{ teachers.length }}
            <template v-if="evalWindow.deadline_text"> · 提交 {{ evalWindow.open_text }} ~ {{ evalWindow.deadline_text }}</template>
          </span>
          <el-button type="primary" :loading="loading"
                     :disabled="!batch.running || evalWindow.status !== 'open'" @click="doSubmitEval">
            {{ evalBtnText }}
          </el-button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { evalLogin, evalIndex, evalChangePwd, evalSubmitEval, evalSubmitAsk, evalSubmitTalk } from '@/api/eval.js'

const TOKEN_KEY = 'eval_token'

export default {
  name: 'DtEvalStudent',
  data() {
    return {
      page: 'login',          // login | pwd | eval
      loading: false,
      batchName: '教学常规评价',
      activeTab: 'ask',       // ask=每周提问上报 | talk=每周导师谈话 | eval=每月教学评价
      pendingTab: '',         // ?tab=xxx 直达指定页签

      student: {},
      batch: {},
      period: {},
      items: [],
      teachers: [],
      // 导师谈话（每周一次，独立提交）
      talk: { done: 0, mentor_name: '', mentor_last: '', talk_count: null, content: '', weeks_reported: 0 },
      talkForm: { mentor_name: '', talk_count: undefined, content: '' },

      loginForm: { account: '', password: '' },
      pwdForm: { newPassword: '', confirmPassword: '' },
      oldPassword: ''         // 首次改密时用来校验原密码（登录时输入的那个）
    }
  },
  computed: {
    askFilledCount() {
      return this.teachers.filter(t => this.isAskFilled(t)).length
    },
    evalFilledCount() {
      return this.teachers.filter(t => this.isEvalFilled(t)).length
    },
    weekReportedBefore() {
      return this.teachers.some(t => t.ask_done)
    },
    monthEvaluatedBefore() {
      return this.teachers.some(t => t.submitted)
    },
    askWindow() {
      return this.period.ask_window || {}
    },
    evalWindow() {
      return this.period.eval_window || {}
    },
    // 导师下拉：只列本班任课教师；历史上填过但已不在名单里的姓名也保留，避免旧记录被清空
    mentorOptions() {
      const list = this.teachers.map((t) => {
        const course = String(t.course_names || '').split(' / ')[0]
        return {
          name: t.name,
          label: course ? `${t.name}（${course}）` : t.name
        }
      })
      const cur = this.talkForm.mentor_name
      if (cur && !list.some((m) => m.name === cur)) {
        list.unshift({ name: cur, label: `${cur}（原填报）` })
      }
      return list
    },
    askBtnText() {
      if (!this.batch.running) return '当前不在评价时间内'
      if (this.askWindow.status === 'pending') return '本周上报未开始'
      if (this.askWindow.status === 'closed') return '本周上报已截止'
      return this.weekReportedBefore ? '重新上报本周' : '提交本周提问次数'
    },
    evalBtnText() {
      if (!this.batch.running) return '当前不在评价时间内'
      if (this.evalWindow.status === 'pending') return '本月评价未开始'
      if (this.evalWindow.status === 'closed') return '本月评价已截止'
      return this.monthEvaluatedBefore ? '重新提交本月评价' : '提交本月评价'
    },
    talkBtnText() {
      if (!this.batch.running) return '当前不在评价时间内'
      if (this.askWindow.status === 'pending') return '本周上报未开始'
      if (this.askWindow.status === 'closed') return '本周上报已截止'
      return this.talk.done ? '重新提交本周谈话' : '提交本周谈话'
    }
  },
  mounted() {
    const search = window.location.search
    // 支持 ?token=xxx 直达（例如从其他系统跳转过来）
    const m = search.match(/[?&]token=([^&]+)/)
    if (m) {
      localStorage.setItem(TOKEN_KEY, decodeURIComponent(m[1]))
      window.history.replaceState(null, '', window.location.pathname)
    }
    // 支持 ?tab=ask|talk|eval 直达某个页签
    const t = search.match(/[?&]tab=(ask|talk|eval)/)
    if (t) {
      this.pendingTab = t[1]
    }
    if (localStorage.getItem(TOKEN_KEY)) {
      this.loadIndex()
    }
  },
  methods: {
    /* --- 登录 --- */
    doLogin() {
      if (!this.loginForm.account || !this.loginForm.password) {
        this.$message.warning('请输入账号和密码')
        return
      }
      this.loading = true
      evalLogin(this.loginForm).then((res) => {
        this.loading = false
        if (res.code !== 0) {
          this.$message.error(res.msg)
          return
        }
        localStorage.setItem(TOKEN_KEY, res.data.token)
        this.student = res.data.student
        this.oldPassword = this.loginForm.password
        this.loginForm.password = ''

        if (res.data.must_change_pwd) {
          // 首次登录（还是默认密码）→ 强制改密
          this.page = 'pwd'
        } else {
          this.loadIndex()
        }
      }).catch(() => {
        this.loading = false
        this.$message.error('网络异常，请稍后重试')
      })
    },

    /* --- 修改密码 --- */
    doChangePwd() {
      if (this.pwdForm.newPassword.length < 6) {
        this.$message.warning('新密码至少 6 位')
        return
      }
      if (this.pwdForm.newPassword !== this.pwdForm.confirmPassword) {
        this.$message.warning('两次输入的密码不一致')
        return
      }
      this.loading = true
      evalChangePwd({
        old_password: this.oldPassword || this.student.default_pwd || '',
        new_password: this.pwdForm.newPassword
      }).then((res) => {
        this.loading = false
        if (res.code !== 0) {
          this.$message.error(res.msg)
          return
        }
        this.$message.success('密码修改成功')
        this.page = 'eval'
        this.loadIndex()
      }).catch(() => {
        this.loading = false
        this.$message.error('网络异常，请稍后重试')
      })
    },

    /* --- 拉取评价首页数据 --- */
    loadIndex() {
      this.loading = true
      evalIndex().then((res) => {
        this.loading = false
        if (res.code === 401) {
          this.logout()
          return
        }
        if (res.code !== 0) {
          this.page = 'eval'
          this.$message.warning(res.msg)
          return
        }
        const data = res.data
        this.student = data.student
        this.batch = data.batch
        this.batchName = data.batch.name
        this.period = data.period || {}
        this.items = data.items
        this.teachers = data.teachers.map((t) => {
          const scores = {}
          data.items.forEach((it) => {
            const v = t.scores && t.scores[it.id] ? parseInt(t.scores[it.id], 10) : 0
            scores[it.id] = v // 先建好键，保证响应式
          })
          return {
            teacher_info_id: t.teacher_info_id,
            name: t.name,
            course_names: t.course_names,
            submitted: t.submitted,                       // 本月已评价
            ask_done: t.ask_this_week !== null && t.ask_this_week !== undefined, // 本周已上报
            comment: t.comment || '',
            ask_input: t.ask_this_week === null || t.ask_this_week === undefined ? undefined : t.ask_this_week,
            ask_total: t.ask_total || 0,
            scores: scores
          }
        })
        // 本周导师谈话（有记录则回填）
        this.talk = data.talk || { done: 0, mentor_name: '', mentor_last: '', talk_count: null, content: '', weeks_reported: 0 }
        // 本周未填报时，默认带出上次填报的导师（每个学生只有一位导师）
        this.talkForm.mentor_name = this.talk.mentor_name || this.talk.mentor_last || ''
        this.talkForm.talk_count = this.talk.talk_count === null || this.talk.talk_count === undefined
          ? undefined : this.talk.talk_count
        this.talkForm.content = this.talk.content || ''

      // 默认停在还没完成的页签：提问 → 导师谈话 → 评价
      if (this.pendingTab) {
        this.activeTab = this.pendingTab
        this.pendingTab = ''
      } else if (!this.weekReportedBefore) {
        this.activeTab = 'ask'
      } else if (!this.talk.done) {
        this.activeTab = 'talk'
      } else {
        this.activeTab = 'eval'
      }
        this.page = 'eval'
        // 页签渲染出来后，给标签栏挂上触摸滑动（手机端左右滑）
        this.$nextTick(() => this.initTabTouch())
      }).catch(() => {
        this.loading = false
        this.page = 'login'
        this.$message.error('网络异常，请稍后重试')
      })
    },

    /* --- 手机端：页签标签栏支持手指左右滑动 --- */
    initTabTouch() {
      const scrollEl = this.$el && this.$el.querySelector('.eval-tabs .el-tabs__nav-scroll')
      if (!scrollEl || scrollEl.dataset.touchBound) return
      scrollEl.dataset.touchBound = '1'

      const navEl = () => scrollEl.querySelector('.el-tabs__nav')
      // 标签总宽超出可见宽度多少（0 = 没有溢出，不用滑）
      const maxOffset = () => {
        const nav = navEl()
        return nav ? Math.max(0, nav.scrollWidth - scrollEl.clientWidth) : 0
      }
      const currentOffset = () => {
        const m = /translateX\((-?[\d.]+)px\)/.exec(navEl().style.transform || '')
        return m ? parseFloat(m[1]) : 0
      }
      const apply = (v) => {
        const x = Math.min(0, Math.max(-maxOffset(), v))
        navEl().style.transform = 'translateX(' + x + 'px)'
      }

      let startX = 0, startY = 0, startOffset = 0, tracking = false, horizontal = null

      scrollEl.addEventListener('touchstart', (e) => {
        if (maxOffset() <= 0) return
        tracking = true
        horizontal = null
        startX = e.touches[0].clientX
        startY = e.touches[0].clientY
        startOffset = currentOffset()
        navEl().style.transition = 'none'   // 手指跟手，去掉动画
      }, { passive: true })

      scrollEl.addEventListener('touchmove', (e) => {
        if (!tracking) return
        const dx = e.touches[0].clientX - startX
        const dy = e.touches[0].clientY - startY
        if (horizontal === null) {
          if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return
          // 先判断意图：横向滑才接管，竖向仍让页面正常滚动
          horizontal = Math.abs(dx) > Math.abs(dy)
          if (!horizontal) { tracking = false; return }
        }
        if (!horizontal) return
        e.preventDefault()
        apply(startOffset + dx)
      }, { passive: false })

      const stop = () => {
        if (!tracking) return
        tracking = false
        navEl().style.transition = ''
      }
      scrollEl.addEventListener('touchend', stop, { passive: true })
      scrollEl.addEventListener('touchcancel', stop, { passive: true })
    },

    /* --- 填写状态 --- */
    isAskFilled(t) {
      return t.ask_input !== undefined && t.ask_input !== null && t.ask_input !== ''
    },
    isEvalFilled(t) {
      return this.items.every((it) => t.scores[it.id] > 0)
    },

    /* --- 提交本周提问次数 --- */
    doSubmitAsk() {
      for (let i = 0; i < this.teachers.length; i++) {
        if (!this.isAskFilled(this.teachers[i])) {
          this.$message.warning('请填写「' + this.teachers[i].name + '」老师本周的提问次数（没有填 0）')
          return
        }
      }

      this.$confirm('每周上报一次，提交后本周内仍可修改，确定提交吗？', '提示', {
        customClass: 'eval-confirm-box',
        confirmButtonText: '确定提交',
        cancelButtonText: '再检查一下',
        type: 'warning'
      }).then(() => {
        const items = this.teachers.map((t) => {
          return {
            teacher_info_id: t.teacher_info_id,
            ask_count: t.ask_input
          }
        })

        this.loading = true
        evalSubmitAsk({ items: items }).then((res) => {
          this.loading = false
          if (res.code !== 0) {
            this.$message.error(res.msg)
            return
          }
          this.$message.success(res.msg)
          this.loadIndex()
        }).catch(() => {
          this.loading = false
          this.$message.error('网络异常，请稍后重试')
        })
      }).catch(() => {})
    },

    /* --- 提交本周导师谈话 --- */
    doSubmitTalk() {
      const f = this.talkForm
      if (!f.mentor_name) {
        this.$message.warning('请选择你的导师')
        return
      }
      if (f.talk_count === undefined || f.talk_count === null || f.talk_count === '') {
        this.$message.warning('请填写本周与导师谈话的次数（没有请填 0）')
        return
      }
      if (f.talk_count > 0 && !String(f.content || '').trim()) {
        this.$message.warning('有谈话请简要填写谈话内容')
        return
      }

      this.$confirm('每周填报一次，提交后本周内仍可修改，确定提交吗？', '提示', {
        customClass: 'eval-confirm-box',
        confirmButtonText: '确定提交',
        cancelButtonText: '再检查一下',
        type: 'warning'
      }).then(() => {
        this.loading = true
        evalSubmitTalk({
          mentor_name: f.mentor_name,
          talk_count: f.talk_count,
          content: f.content || ''
        }).then((res) => {
          this.loading = false
          if (res.code !== 0) {
            this.$message.error(res.msg)
            return
          }
          this.$message.success(res.msg)
          this.loadIndex()
        }).catch(() => {
          this.loading = false
          this.$message.error('网络异常，请稍后重试')
        })
      }).catch(() => {})
    },

    /* --- 提交本月评价 --- */
    doSubmitEval() {
      for (let i = 0; i < this.teachers.length; i++) {
        if (!this.isEvalFilled(this.teachers[i])) {
          this.$message.warning('请先完成「' + this.teachers[i].name + '」老师的评分')
          return
        }
      }

      this.$confirm('每月评价一次，提交后本月内仍可修改，确定提交吗？', '提示', {
        customClass: 'eval-confirm-box',
        confirmButtonText: '确定提交',
        cancelButtonText: '再检查一下',
        type: 'warning'
      }).then(() => {
        const items = this.teachers.map((t) => {
          return {
            teacher_info_id: t.teacher_info_id,
            scores: t.scores,
            comment: t.comment || ''
          }
        })

        this.loading = true
        evalSubmitEval({ items: items }).then((res) => {
          this.loading = false
          if (res.code !== 0) {
            this.$message.error(res.msg)
            return
          }
          this.$message.success(res.msg)
          this.loadIndex()
        }).catch(() => {
          this.loading = false
          this.$message.error('网络异常，请稍后重试')
        })
      }).catch(() => {})
    },

    /* --- 日期简写：2026-10-26 → 10-26 --- */
    shortDate(d) {
      return d ? String(d).slice(5) : ''
    },

    /* --- 退出 --- */
    logout() {
      localStorage.removeItem(TOKEN_KEY)
      this.page = 'login'
      this.student = {}
      this.teachers = []
    }
  }
}
</script>

<style scoped lang="scss">
.eval-student {
  min-height: 100vh;
  background: #f2f4f8;
  color: #1f2733;
  font-size: 15px;
}

.muted {
  color: #8a94a6;
  font-size: 13px;
}

/* ---------------- 登录 / 改密 ---------------- */

.center-wrap {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.login-card {
  width: 100%;
  max-width: 400px;
}

.title {
  margin: 0;
  font-size: 22px;
  text-align: center;
  letter-spacing: 1px;
}

.sub {
  margin: 8px 0 22px;
  text-align: center;
  color: #8a94a6;
  font-size: 13px;
}

.block-btn {
  width: 100%;
  margin-top: 6px;
}

.tip {
  margin: 14px 0 0;
  font-size: 12px;
  line-height: 1.6;
  color: #9aa3b5;
}

/* ---------------- 评价主页 ---------------- */

.topbar {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #fff;
  border-bottom: 1px solid #e8ecf4;
}

.topbar-inner {
  max-width: 1080px;
  margin: 0 auto;
  height: 56px;
  padding: 0 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.who {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
}

.right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.batch {
  color: #8a94a6;
  font-size: 13px;
}

.body {
  max-width: 1080px;
  margin: 0 auto;
  padding: 18px;
}

.notice {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #fff;
  border-left: 4px solid #3b6ef0;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;

  b {
    font-weight: 600;
    margin-right: 10px;
  }

  &.slim {
    border-left-color: #67c23a;
    margin-bottom: 12px;
  }
}

.eval-tabs {
  .tab-dot {
    margin-left: 2px;
  }
  .tab-ok {
    color: #67c23a;
    margin-left: 2px;
  }
}

.teacher-card {
  margin-bottom: 14px;
  border-radius: 10px;
}

.t-head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.t-index {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #f0f3fa;
  color: #8a94a6;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}

.t-name {
  font-size: 17px;
  font-weight: 600;
}

.done-flag {
  margin-left: auto;
}

.item-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 6px 0;
}

.item-name {
  color: #37415a;
  font-size: 14px;
}

.ask-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 4px 0;
}

.talk-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 8px 0;

  &.talk-content {
    align-items: flex-start;

    .el-textarea {
      flex: 1;
      min-width: 260px;
    }
  }
}

.talk-label {
  width: 96px;
  flex: none;
  color: #55607a;
  font-size: 14px;
}

.total-tip {
  margin-left: 8px;
}

.extra {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 10px;
  padding-top: 12px;
  border-top: 1px dashed #e8ecf4;
}

.ask {
  color: #55607a;
  font-size: 14px;
}

.comment {
  flex: 1;
  min-width: 220px;
}

.empty {
  text-align: center;
  color: #8a94a6;
  padding: 40px 0;
}

/* ---------------- 底部提交条 ---------------- */

.footer-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 50; /* 高于卡片内输入框等绝对定位元素，避免被盖住 */
  background: rgba(255, 255, 255, 0.96);
  border-top: 1px solid #e8ecf4;
}

.footer-inner {
  max-width: 1080px;
  margin: 0 auto;
  height: 64px;
  padding: 0 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

@media (max-width: 640px) {
  .item-row {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }
  .notice {
    flex-direction: column;
    align-items: flex-start;
  }
  .body {
    padding: 12px;
  }

  /* 手机端：三个页签平分一行宽度，正常情况下不再出现左右滚动箭头 */
  .eval-tabs {
    .el-tabs__nav {
      width: 100%;
      display: flex;
    }
    .el-tabs__item {
      flex: 1;
      padding: 0 4px;
      text-align: center;
      font-size: 13px;
      line-height: 40px;
      height: 40px;

      i {
        font-size: 14px;
      }
    }
    .tab-dot {
      margin-left: 1px;
    }
  }

  /* 手机端：底部栏文字太长时允许换行，不再挤压提交按钮 */
  .footer-inner {
    height: auto;
    min-height: 56px;
    padding: 8px 12px;
    gap: 10px;

    .muted {
      flex: 1;
      min-width: 0;
      font-size: 12px;
      line-height: 1.5;
    }
    .el-button {
      flex-shrink: 0;
      margin-left: 0;
    }
  }
}
</style>

<style lang="scss">
/* 确认框 / 提示条挂在 body 上，scoped 样式作用不到，需要全局样式；仅手机端收窄 */
@media (max-width: 640px) {
  .eval-confirm-box {
    width: 88% !important;
    max-width: 420px;

    .el-message-box__message {
      word-break: break-all;
    }
    .el-message-box__btns .el-button {
      padding: 9px 14px;
    }
  }

  .el-message {
    max-width: 88vw;
    min-width: 0;
    box-sizing: border-box;
    word-break: break-all;
  }
}
</style>
